import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "motion/react";
import type React from "react";
import {
	EASE,
	Reveal,
	RevealGroup,
	RevealItem,
	RiseWords,
	WordsInView,
} from "@/components/motion/motion";
import {
	Container,
	KeyValues,
	PastelLabel,
	RuledCard,
	SectionTitle,
	StackLine,
} from "@/components/paper/paper";
import { JourneyTimeline } from "@/components/scenes/JourneyTimeline";
import { MigrationSceneLoop } from "@/components/scenes/MigrationScene";
import {
	ChabanScene,
	QuestoveryScene,
} from "@/components/scenes/ProductScenes";
import { Button } from "@/components/ui/button";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	CONTACT_EMAIL,
	CV_PDF_URL,
	GITHUB_URL,
	LINKEDIN_URL,
	SCHEDULE_VISIO_URL,
} from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

type SectionProps = { n: string; anchor?: string; className?: string };

export function Section({
	anchor,
	className,
	children,
}: {
	anchor?: string;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<section id={anchor} className={cn("py-14 md:py-20", className)}>
			<Container>{children}</Container>
		</section>
	);
}

/* ───────────── Profile ───────────── */

export function ProfileSection({
	n,
	anchor = "profile",
	className,
}: SectionProps) {
	const { t, dict } = useI18n();
	const items = [
		dict.home.profile.specialty,
		dict.home.profile.production,
		dict.home.profile.product,
		dict.home.profile.training,
	];
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle n={n} title={t((t) => t.home.profile.title)} />
			<RevealGroup
				as="dl"
				className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7"
			>
				{items.map((item) => (
					<RevealItem key={item.label}>
						<dt>
							<PastelLabel>{item.label}</PastelLabel>
						</dt>
						<dd className="mt-3 leading-relaxed text-ink-2">{item.value}</dd>
					</RevealItem>
				))}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Journey ───────────── */

export function JourneySection({
	n,
	anchor = "journey",
	className,
}: SectionProps) {
	const { t } = useI18n();
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle
				n={n}
				title={t((t) => t.home.journey.title)}
				subtitle={t((t) => t.home.journey.subtitle)}
			/>
			<JourneyTimeline className="mt-8" />
		</Section>
	);
}

/* ───────────── Missions ───────────── */

export type MissionKey = "ccp" | "mobiapps" | "vaerdict" | "linote" | "silbo";

function MissionBody({ mission }: { mission: MissionKey }) {
	const { dict } = useI18n();
	const m = dict.home.missions[mission];
	const labels = dict.home.missions.labels;
	const rows: { label: string; value: string; result?: boolean }[] = [
		{ label: labels.context, value: m.context },
		{ label: labels.action, value: m.action },
	];
	if ("result" in m)
		rows.push({ label: labels.result, value: m.result, result: true });

	return (
		<>
			<header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
				<h3 className="font-mono text-lg font-semibold tracking-[0.03em] text-ink">
					{m.company}
				</h3>
				<p className="font-mono text-[0.78rem] text-ink-2">{m.period}</p>
			</header>
			<div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4">
				<p className="text-ink-2">{m.role}</p>
				<StackLine>{m.stack}</StackLine>
			</div>
			<dl className="mt-4 grid grid-cols-[5.5rem_1fr] gap-x-2 gap-y-2.5">
				{rows.map((row) => (
					<div key={row.label} className="contents">
						<dt
							className={cn(
								"pt-0.5 font-mono text-[0.74rem]",
								row.result ? "text-ink" : "text-ink-3",
							)}
						>
							{row.label} ›
						</dt>
						<dd
							className={cn(
								"leading-relaxed",
								row.result && "font-medium text-ink",
							)}
						>
							{row.value}
						</dd>
					</div>
				))}
			</dl>
		</>
	);
}

export function MissionsSection({
	n,
	anchor = "missions",
	className,
	featured = "ccp",
	grid = ["mobiapps", "vaerdict", "linote", "silbo"],
}: SectionProps & { featured?: MissionKey; grid?: MissionKey[] }) {
	const { t } = useI18n();
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle
				n={n}
				title={t((t) => t.home.missions.title)}
				subtitle={t((t) => t.home.missions.subtitle)}
			/>
			<Reveal className="mt-8">
				<RuledCard
					as="article"
					className="sheet-lift relative grid items-center gap-8 p-6 md:grid-cols-[1fr_minmax(16rem,22rem)] md:p-8"
				>
					<motion.span
						className="pastel-bar absolute top-0 bottom-0 left-0 w-1.5 origin-top"
						initial={{ scaleY: 0 }}
						whileInView={{ scaleY: 1 }}
						viewport={{ once: true, margin: "0px 0px -12% 0px" }}
						transition={{ duration: 1.2, ease: EASE, delay: 0.2 }}
						aria-hidden="true"
					/>
					<div>
						<MissionBody mission={featured} />
					</div>
					<MigrationSceneLoop className="w-full" />
				</RuledCard>
			</Reveal>
			<RevealGroup
				className={cn("mt-5 grid gap-5", grid.length > 1 && "md:grid-cols-2")}
			>
				{grid.map((mission) => (
					<RevealItem
						key={mission}
						as="article"
						className="sheet-lift border border-rule-2 bg-paper-2/55 p-6"
					>
						<MissionBody mission={mission} />
					</RevealItem>
				))}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Testimonials ───────────── */

export type TestimonialKey = (typeof TESTIMONIALS)[number];

const TESTIMONIALS = [
	"eric",
	"julie",
	"matthieu",
	"thomas",
	"romuald",
	"antoine",
] as const;

export function TestimonialsSection({
	n,
	anchor = "testimonials",
	className,
	title,
	keys = TESTIMONIALS,
}: SectionProps & { title?: string; keys?: readonly TestimonialKey[] }) {
	const { t, dict, locale } = useI18n();
	const [open, close] =
		locale === "fr-FR" ? ["«\u202f", "\u202f»"] : ["“", "”"];
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle
				n={n}
				title={title ?? t((t) => t.home.testimonialsSection.title)}
				subtitle={t((t) => t.home.testimonialsSection.subtitle)}
			/>
			<RevealGroup className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
				{keys.map((key, i) => {
					const item = dict.home.testimonials[key];
					return (
						<RevealItem
							key={key}
							as="article"
							feed
							className="sheet-lift relative flex flex-col border border-rule-2 bg-paper-2/70 px-6 pt-7 pb-6"
						>
							<span
								className="absolute inset-x-0 top-0 h-2 bg-[radial-gradient(circle,var(--paper)_2px,transparent_2.5px)] bg-[length:10px_8px] bg-repeat-x"
								aria-hidden="true"
							/>
							<p className="font-mono text-[0.72rem] text-ink-3">
								#{String(i + 1).padStart(2, "0")}
							</p>
							<blockquote className="mt-3 flex-1 leading-relaxed text-ink">
								<p>
									{open}
									{item.quote}
									{close}
								</p>
							</blockquote>
							<p className="mt-4 border-t border-dashed border-rule-2 pt-3 font-mono text-[0.78rem] text-ink-2">
								{item.name}
							</p>
						</RevealItem>
					);
				})}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Services ───────────── */

const SERVICES = ["team", "takeover", "creation", "lead"] as const;

export function ServicesSection({
	n,
	anchor = "services",
	className,
}: SectionProps) {
	const { dict } = useI18n();
	const s = dict.home.services;
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle n={n} title={s.title} subtitle={s.subtitle} />
			<RevealGroup className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
				{SERVICES.map((key, i) => (
					<RevealItem
						key={key}
						as="article"
						className="sheet-lift border border-rule-2 bg-paper-2/55 p-6"
					>
						<p className="font-mono text-[0.75rem] text-ink-3">
							{String(i + 1).padStart(2, "0")}
						</p>
						<h3 className="mt-3 font-mono text-lg font-semibold text-ink">
							{s[key].title}
						</h3>
						<p className="mt-3 leading-relaxed text-ink-2">
							{s[key].description}
						</p>
					</RevealItem>
				))}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Focus: Expo migration and Questovery ───────────── */

export function FocusSection({ n, anchor = "focus", className }: SectionProps) {
	const { t, dict, localeParam } = useI18n();
	const { capture } = useAnalytics();
	const f = dict.home.focus;
	const params = localeParam ? { locale: localeParam } : {};
	const cards = [
		{
			key: "migration" as const,
			to: "/{-$locale}/expo-migration" as const,
			example: false,
			scene: (
				<div className="grid h-full place-items-center p-6">
					<MigrationSceneLoop className="w-full max-w-sm" />
				</div>
			),
		},
		{
			key: "questovery" as const,
			to: "/{-$locale}/questovery" as const,
			example: true,
			scene: <QuestoveryScene />,
		},
	];

	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle n={n} title={f.title} subtitle={f.subtitle} />
			<RevealGroup className="mt-8 grid gap-6 md:grid-cols-2">
				{cards.map(({ key, to, scene, example }) => {
					const card = f[key];
					return (
						<RevealItem
							key={key}
							as="article"
							className="group sheet-lift relative flex flex-col border border-rule-2 bg-paper-2/55"
						>
							<div className="relative h-56 overflow-hidden border-b border-rule-2 md:h-64">
								<div className="h-full w-full transition-[scale] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]">
									{scene}
								</div>
								<span className="image-frame-overlay" aria-hidden="true" />
								{example ? (
									<span className="absolute bottom-2.5 left-3 z-10 bg-paper-2/90 px-1.5 py-0.5 font-mono text-[0.66rem] text-ink-3">
										{t((t) => t.common.example)}
									</span>
								) : null}
							</div>
							<div className="flex flex-1 flex-col p-6">
								<header className="flex flex-wrap items-baseline justify-between gap-x-4">
									<h3 className="font-mono text-xl font-semibold text-ink">
										{card.name}
									</h3>
									<p className="font-mono text-[0.78rem] text-ink-2">
										{card.meta}
									</p>
								</header>
								<p className="mt-3 leading-relaxed text-ink-2">{card.lede}</p>
								<Link
									to={to}
									params={params}
									className="mt-auto inline-flex items-center gap-1.5 pt-5 font-mono text-[0.85rem] text-ink underline decoration-rule-2 underline-offset-4 after:absolute after:inset-0 hover:decoration-ink"
									onClick={() =>
										capture(ANALYTICS_EVENTS.ctaClick, {
											cta: `focus_${key}`,
											placement: "home_focus",
											href: to,
										})
									}
								>
									{card.link}
									<ArrowRight className="size-3.5" aria-hidden="true" />
								</Link>
							</div>
						</RevealItem>
					);
				})}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Products ───────────── */

export function ProductsSection({
	n,
	anchor = "products",
	className,
}: SectionProps) {
	const { t, dict } = useI18n();
	const { capture } = useAnalytics();
	const products = [
		{
			key: "questovery" as const,
			Scene: QuestoveryScene,
			href: "https://questovery.com/fr",
			points: ["offline", "ai", "sales"] as const,
		},
		{
			key: "chaban" as const,
			Scene: ChabanScene,
			href: "https://pont-chaban-delmas.com",
			points: ["alerts", "effect", "stack"] as const,
		},
	];

	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle
				n={n}
				title={t((t) => t.home.products.title)}
				subtitle={t((t) => t.home.products.subtitle)}
			/>
			<RevealGroup className="mt-8 grid gap-6 md:grid-cols-2">
				{products.map(({ key, Scene, href, points }) => {
					const p = dict.home.products[key];
					const pointValues = p.points as Record<string, string>;
					return (
						<RevealItem
							key={key}
							as="article"
							className="group sheet-lift flex flex-col border border-rule-2 bg-paper-2/55"
						>
							<div className="relative h-56 overflow-hidden border-b border-rule-2 md:h-64">
								<div className="h-full w-full transition-[scale] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]">
									<Scene />
								</div>
								<span className="image-frame-overlay" aria-hidden="true" />
								<span className="absolute bottom-2.5 left-3 z-10 bg-paper-2/90 px-1.5 py-0.5 font-mono text-[0.66rem] text-ink-3">
									{t((t) => t.common.example)}
								</span>
							</div>
							<div className="flex flex-1 flex-col p-6">
								<header className="flex flex-wrap items-baseline justify-between gap-x-4">
									<h3 className="font-mono text-xl font-semibold text-ink">
										{p.name}
									</h3>
									<p className="font-mono text-[0.78rem] text-ink-2">
										{p.meta}
									</p>
								</header>
								<p className="mt-3 leading-relaxed text-ink-2">{p.lede}</p>
								<ul className="mt-4 space-y-1.5">
									{points.map((point) => (
										<li key={point} className="relative pl-5">
											<span
												className="absolute left-0 text-ink-3"
												aria-hidden="true"
											>
												–
											</span>
											{pointValues[point]}
										</li>
									))}
								</ul>
								<StackLine className="mt-5">{p.stack}</StackLine>
								<a
									href={href}
									target="_blank"
									rel="noreferrer"
									className="mt-auto inline-flex items-center gap-1.5 pt-4 font-mono text-[0.85rem] text-ink underline decoration-rule-2 underline-offset-4 hover:decoration-ink"
									onClick={() =>
										capture(ANALYTICS_EVENTS.ctaClick, {
											cta: `product_${key}`,
											placement: "products",
											href,
										})
									}
								>
									{p.link}
									<ArrowUpRight className="size-3.5" aria-hidden="true" />
								</a>
							</div>
						</RevealItem>
					);
				})}
			</RevealGroup>
		</Section>
	);
}

/* ───────────── Skills ───────────── */

export function SkillsSection({
	n,
	anchor = "skills",
	className,
}: SectionProps) {
	const { dict } = useI18n();
	const s = dict.home.skills;
	const groups = [s.mobile, s.web, s.backend, s.ai, s.product];
	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle n={n} title={s.title} />
			<RevealGroup className="mt-8 grid gap-7 sm:grid-cols-2 lg:grid-cols-[1.35fr_0.85fr_1.35fr_1fr_1fr]">
				{groups.map((group) => (
					<RevealItem key={group.title}>
						<h3 className="font-mono text-[0.78rem] font-medium tracking-[0.1em] text-ink uppercase">
							<span className="text-ink-3" aria-hidden="true">
								&gt;{" "}
							</span>
							{group.title}
						</h3>
						<p className="mt-2 leading-relaxed text-ink-2">{group.items}</p>
					</RevealItem>
				))}
			</RevealGroup>
			<p className="mt-10 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-dashed border-rule-2 pt-5 text-ink-2">
				<span className="font-mono text-[0.74rem] tracking-[0.1em] text-ink uppercase">
					{s.educationLabel}
				</span>
				<span>
					<b className="font-semibold text-ink">{s.master}</b>, {s.masterDetail}
				</span>
				<span className="text-ink-4" aria-hidden="true">
					/
				</span>
				<span>
					<b className="font-semibold text-ink">{s.selfTaught}</b>,{" "}
					{s.selfTaughtDetail}
				</span>
			</p>
		</Section>
	);
}

/* ───────────── Contact ───────────── */

export function ContactSection({
	n,
	anchor = "contact",
	className,
}: SectionProps) {
	const { t } = useI18n();
	const { capture } = useAnalytics();
	const track = (cta: string, href: string) => () =>
		capture(ANALYTICS_EVENTS.ctaClick, { cta, placement: "contact", href });

	return (
		<Section anchor={anchor} className={className}>
			<SectionTitle n={n} title={t((t) => t.home.contact.title)} />
			<Reveal className="mt-8 grid gap-10 md:grid-cols-[1fr_auto] md:items-end">
				<div>
					<WordsInView
						text={t((t) => t.home.contact.body)}
						className="max-w-2xl text-xl leading-relaxed text-ink md:text-2xl"
					/>
					<div className="mt-8 flex flex-wrap gap-3">
						<Button asChild size="lg">
							<a
								href={SCHEDULE_VISIO_URL}
								target="_blank"
								rel="noreferrer"
								onClick={track("schedule_call", SCHEDULE_VISIO_URL)}
							>
								{t((t) => t.common.bookCall)}
							</a>
						</Button>
						<Button asChild size="lg" variant="outline">
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								onClick={track("email", `mailto:${CONTACT_EMAIL}`)}
							>
								{t((t) => t.common.sendEmail)}
							</a>
						</Button>
						<Button asChild size="lg" variant="ghost">
							<a href={CV_PDF_URL} onClick={track("cv_pdf", CV_PDF_URL)}>
								{t((t) => t.common.downloadCv)}
								<ArrowDown aria-hidden="true" />
							</a>
						</Button>
					</div>
				</div>
				<KeyValues
					className="border-l border-dashed border-rule-2 pl-5"
					items={[
						{
							key: "email",
							value: CONTACT_EMAIL,
							href: `mailto:${CONTACT_EMAIL}`,
						},
						{
							key: "linkedin",
							value: "in/simon-boisset",
							href: LINKEDIN_URL,
							external: true,
						},
						{
							key: "github",
							value: "simonboisset",
							href: GITHUB_URL,
							external: true,
						},
					]}
				/>
			</Reveal>
		</Section>
	);
}

/* ───────────── Page hero for inner pages ───────────── */

export function PageHero({
	kicker,
	title,
	intro,
	children,
}: {
	kicker: string;
	title: string;
	intro: string;
	children?: React.ReactNode;
}) {
	return (
		<section className="pt-12 pb-6 md:pt-20">
			<Container>
				<p className="text-kicker line-rise">{kicker}</p>
				<h1 className="mt-4 max-w-4xl font-mono text-[clamp(1.9rem,4.6vw,3.4rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-balance text-ink">
					<RiseWords text={title} delay={0.12} step={0.045} />
				</h1>
				<p
					className="line-rise mt-6 max-w-2xl text-lg leading-relaxed text-ink-2"
					style={{ animationDelay: "0.45s" }}
				>
					{intro}
				</p>
				{children ? (
					<div className="line-rise" style={{ animationDelay: "0.6s" }}>
						{children}
					</div>
				) : null}
			</Container>
		</section>
	);
}

export function InternalLink({
	to,
	children,
}: {
	to:
		| "/{-$locale}/expo-migration"
		| "/{-$locale}/questovery"
		| "/{-$locale}/about";
	children: React.ReactNode;
}) {
	const { localeParam } = useI18n();
	return (
		<Button asChild size="lg" variant="outline">
			<Link to={to} params={localeParam ? { locale: localeParam } : {}}>
				{children}
			</Link>
		</Button>
	);
}
